import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  KernelConfig, ParamSelection, PolygonSelection, ExampleEntry
} from "../types/lenia";
import { setupCanvas } from "../utils/canvasUtils";
import { loadPolygonPhases } from "../utils/leniaDataAdapter";
import { usePhaseColors } from "../hooks/usePhaseColors";
import {
  RUN_PHASES, type RunPhase, RUN_PHASE_LABELS, RUN_TO_GLOBAL
} from "../constants/phases";

// Internal distribution type keyed by RunPhase
type Dist = { order: number; chaos: number; max: number; total: number };
type PolygonPhaseData = Record<number, ExampleEntry[]>;

interface Props {
  kernel: KernelConfig;
  params: ParamSelection;
  onPolygonSelect: (selection: PolygonSelection) => void;
  selectedPolygon: PolygonSelection | null;
  setLoading?: (loading: boolean) => void;
  width?: number;
  height?: number;
}

export const PolygonPhaseChart: React.FC<Props> = ({
  kernel, params, onPolygonSelect, selectedPolygon, setLoading,
  width = 960, height = 230
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const focusRef = useRef<HTMLDivElement>(null);

  const [polygonData, setPolygonData] = useState<PolygonPhaseData | null>(null);
  const [loading, setLocalLoading] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);

  const [selectedPhase, setSelectedPhase] = useState<RunPhase>("max"); // default Solitons
  const [selectedSize, setSelectedSize] = useState<number | null>(null);
  const [selectedRunIdx, setSelectedRunIdx] = useState(0);

  const [hoveredBar, setHoveredBar] = useState<{ polygonSize: number; phase: RunPhase } | null>(null);

  const didAutoInitRef = useRef(false);
  const loadAbortRef = useRef<AbortController | null>(null);
  const loadTokenRef = useRef(0);

  // Colors from CSS vars (global phases). We map run-phase → global for rendering.
  const phaseColors = usePhaseColors();
  const colorForRun = useCallback((rp: RunPhase) => phaseColors[RUN_TO_GLOBAL[rp]], [phaseColors]);

  useEffect(() => {
    let t: any;
    if (loading) t = setTimeout(() => setShowOverlay(true), 120);
    else setShowOverlay(false);
    return () => clearTimeout(t);
  }, [loading]);

  // Abortable, race-safe load
  useEffect(() => {
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    const myToken = ++loadTokenRef.current;

    const debounce = setTimeout(async () => {
      setLocalLoading(true);
      setLoading?.(true);
      try {
        const data = await loadPolygonPhases(kernel, params, controller.signal);
        if (loadTokenRef.current !== myToken) return;
        setPolygonData(data);
      } catch (err: any) {
        if (err?.name !== "AbortError") {
          console.error("Polygon phase load error:", err);
          if (!polygonData) setPolygonData(null);
        }
      } finally {
        if (loadTokenRef.current === myToken) {
          setLocalLoading(false);
          setLoading?.(false);
          didAutoInitRef.current = false;
        }
      }
    }, 60);

    return () => {
      clearTimeout(debounce);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kernel, params, setLoading]);

  // Normalize data
  const { sizes, distBySize, entriesBySize } = React.useMemo(() => {
    if (!polygonData) return {
      sizes: [] as number[],
      distBySize: {} as Record<number, Dist>,
      entriesBySize: {} as Record<number, ExampleEntry[]>,
    };
    const base = polygonData as Record<number, ExampleEntry[]>;
    const s: number[] = [];
    const dist: Record<number, Dist> = {};
    const entries: Record<number, ExampleEntry[]> = {};

    Object.entries(base).forEach(([k, v]) => {
      const size = parseInt(k, 10);
      if (Number.isNaN(size)) return;
      const arr = v as ExampleEntry[];
      entries[size] = arr;
      const d: Dist = { order: 0, chaos: 0, max: 0, total: arr.length };
      arr.forEach(ex => {
        const ph = String(ex.phase).toLowerCase() as RunPhase;
        if (ph === "order") d.order++;
        else if (ph === "chaos") d.chaos++;
        else if (ph === "max") d.max++;
      });
      dist[size] = d;
      s.push(size);
    });
    s.sort((a, b) => a - b);
    return { sizes: s, distBySize: dist, entriesBySize: entries };
  }, [polygonData]);

  // Available run-phases overall
  const availablePhases = React.useMemo(() => {
    const set = new Set<RunPhase>();
    for (const sz of sizes) {
      const d = distBySize[sz];
      if (!d) continue;
      (["order", "chaos", "max"] as RunPhase[]).forEach(ph => {
        if (d[ph] && d[ph] > 0) set.add(ph);
      });
    }
    return (["order", "chaos", "max"] as RunPhase[]).filter(ph => set.has(ph));
  }, [sizes, distBySize]);

  // Sizes that contain runs for the selected phase
  const phaseSizes = React.useMemo(() => {
    if (!sizes.length) return [] as number[];
    return sizes.filter(sz => distBySize[sz] && distBySize[sz][selectedPhase] > 0);
  }, [sizes, distBySize, selectedPhase]);

  const commitSelection = useCallback((size: number, phase: RunPhase, runIdx: number) => {
    const all = entriesBySize[size] || [];
    const runs = all.filter(ex => String(ex.phase).toLowerCase() === phase);
    if (!runs.length) return;
    const idx = ((runIdx % runs.length) + runs.length) % runs.length;
    const chosen = runs[idx];
    setSelectedPhase(phase);
    setSelectedSize(size);
    setSelectedRunIdx(idx);
    onPolygonSelect({
      polygon_size: size,
      sample_index: chosen.sample_index,
      phase: chosen.phase,
    });
  }, [entriesBySize, onPolygonSelect]);

  // Sync if parent changes selection
  useEffect(() => {
    if (!selectedPolygon) return;
    const ph = String(selectedPolygon.phase).toLowerCase() as RunPhase;
    const sz = selectedPolygon.polygon_size;

    const all = entriesBySize[sz] || [];
    const runs = all.filter(ex => String(ex.phase).toLowerCase() === ph);
    const idx = runs.findIndex(ex => ex.sample_index === selectedPolygon.sample_index);
    if (availablePhases.includes(ph) && runs.length) {
      setSelectedPhase(ph);
      setSelectedSize(sz);
      if (idx >= 0) setSelectedRunIdx(idx);
    }
  }, [selectedPolygon, entriesBySize, availablePhases]);

  const handlePhaseClick = (ph: RunPhase) => {
    if (!availablePhases.includes(ph)) return;
    const s = sizes.find(sz => (distBySize[sz]?.[ph] ?? 0) > 0);
    if (s != null) commitSelection(s, ph, 0);
    focusRef.current?.focus();
  };

  const currentRuns = React.useMemo(() => {
    if (selectedSize == null) return [] as ExampleEntry[];
    return (entriesBySize[selectedSize] || []).filter(ex => String(ex.phase).toLowerCase() === selectedPhase);
  }, [entriesBySize, selectedSize, selectedPhase]);

  const jumpRun = useCallback((dir: -1 | 1) => {
    if (selectedSize == null || !phaseSizes.length) return;

    const runsHere = currentRuns;
    if (!runsHere.length) return;

    const tryIdx = selectedRunIdx + dir;
    if (tryIdx >= 0 && tryIdx < runsHere.length) {
      commitSelection(selectedSize, selectedPhase, tryIdx);
      return;
    }

    const i = phaseSizes.indexOf(selectedSize);
    for (let step = 1; step <= phaseSizes.length; step++) {
      const j = (i + dir * step + phaseSizes.length) % phaseSizes.length;
      const size = phaseSizes[j];
      const list = (entriesBySize[size] || []).filter(ex => String(ex.phase).toLowerCase() === selectedPhase);
      if (list.length) {
        commitSelection(size, selectedPhase, dir > 0 ? 0 : list.length - 1);
        return;
      }
    }
  }, [selectedSize, selectedRunIdx, phaseSizes, currentRuns, commitSelection, entriesBySize, selectedPhase]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowRight") { e.preventDefault(); jumpRun(1); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); jumpRun(-1); }
  };

  const getCanvasCoords = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  }, []);

  const onMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!sizes.length) return;
    const { x, y } = getCanvasCoords(e);

    const margin = { top: 10, right: 10, bottom: 30, left: 10 };
    const W = width - margin.left - margin.right;
    const H = height - margin.top - margin.bottom;

    if (x < margin.left || x > margin.left + W || y < margin.top || y > margin.top + H) {
      setHoveredBar(null); return;
    }

    const barW = W / sizes.length;
    const idx = Math.floor((x - margin.left) / barW);
    if (idx < 0 || idx >= sizes.length) { setHoveredBar(null); return; }
    const size = sizes[idx];
    const dist = distBySize[size];
    if (!dist || !dist.total) { setHoveredBar(null); return; }

    const relY = (y - margin.top) / H;
    let cum = 0;
    let ph: RunPhase | null = null;
    (["order", "chaos", "max"] as RunPhase[]).forEach(p => {
      if (ph) return;
      const seg = dist[p] / dist.total;
      const segTop = 1 - (cum + seg), segBot = 1 - cum;
      if (relY >= segTop && relY <= segBot) ph = p;
      cum += seg;
    });
    setHoveredBar(ph ? { polygonSize: size, phase: ph } : null);
  }, [sizes, distBySize, getCanvasCoords, width, height]);

  const onClick = useCallback(() => {
    if (!hoveredBar) return;
    const { polygonSize, phase } = hoveredBar;
    commitSelection(polygonSize, phase, 0);
  }, [hoveredBar, commitSelection]);

  // Auto-init once per load: prefer Solitons -> Chaos -> Order
  useEffect(() => {
    if (didAutoInitRef.current) return;
    if (!sizes.length) return;

    const preferred: RunPhase[] = ["max", "chaos", "order"];
    for (const ph of preferred) {
      const s = sizes.find(sz => (distBySize[sz]?.[ph] ?? 0) > 0);
      if (s != null) {
        commitSelection(s, ph, 0);
        didAutoInitRef.current = true;
        return;
      }
    }

    // Fallback
    const smallest = sizes[0];
    const arr = entriesBySize[smallest] || [];
    if (arr.length) {
      const ph = String(arr[0].phase).toLowerCase() as RunPhase;
      commitSelection(smallest, ph, 0);
      didAutoInitRef.current = true;
    }
  }, [sizes, distBySize, entriesBySize, commitSelection]);

  // Render
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = setupCanvas(canvas, width, height);
    if (!ctx) return;

    const margin = { top: 10, right: 10, bottom: 60, left: 30 };
    const W = width - margin.left - margin.right;
    const H = height - margin.top - margin.bottom;

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = "#0b1220";
    ctx.fillRect(0, 0, width, height);

    ctx.fillStyle = "#0f172a";
    ctx.fillRect(margin.left, margin.top, W, H);

    if (sizes.length) {
      const n = sizes.length;
      const barW = W / n;
      const padFrac = n >= 20 ? 0.04 : n >= 12 ? 0.08 : 0.12;
      const pad = Math.max(1, barW * padFrac);
      const innerW = Math.max(1, barW - pad);

      sizes.forEach((size, i) => {
        const d = distBySize[size] ?? { order: 0, chaos: 0, max: 0, total: 0 };
        if (!d || !d.total) return;
        const x = margin.left + i * barW + pad / 2;
        let yBottom = margin.top + H;

        (["order", "chaos", "max"] as RunPhase[]).forEach(ph => {
          const cnt = d[ph];
          if (!cnt) return;
          const h = (cnt / d.total) * H;
          const y = yBottom - h;

          const hovered = hoveredBar && hoveredBar.polygonSize === size && hoveredBar.phase === ph;
          const active = (selectedSize === size && selectedPhase === ph);
          ctx.globalAlpha = hovered || active ? 1 : 0.9;
          ctx.fillStyle = colorForRun(ph);
          ctx.fillRect(x, y, innerW, h);
          if (active) {
            ctx.strokeStyle = "#ffffff";
            ctx.lineWidth = 3;
            ctx.strokeRect(x, y, innerW, h);
          }

          ctx.globalAlpha = 0.35;
          ctx.fillStyle = "#0b1220";
          ctx.fillRect(x, y - 1, innerW, 1);
          ctx.globalAlpha = 1;

          yBottom = y;
        });
      });
    }

    if (sizes.length) {
      ctx.fillStyle = "#cbd5e1";
      ctx.font = '1.0rem ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace';
      ctx.textAlign = "center";
      const n = sizes.length;
      const barW = W / n;
      const labelStep = n > 40 ? 5 : n > 28 ? 3 : n > 18 ? 2 : 1;
      sizes.forEach((size, i) => {
        if (i % labelStep !== 0) return;
        const x = margin.left + i * barW + barW / 2;
        ctx.fillText(String(size), x, margin.top + H + 18);
      });
    }

    // X-axis label
    ctx.fillStyle = "#cbd5e1";
    ctx.font = '1.1rem ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace';
    ctx.textAlign = "center";
    ctx.fillText("random polygon size", margin.left + W / 2, margin.top + H + 40);

    // Y-axis label (rotated)
    ctx.save();
    ctx.translate(margin.left - 10, margin.top + H / 2 + 20);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = "center";
    ctx.fillText("# phases", 0, 0);
    ctx.restore();

    ctx.strokeStyle = "#334155";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(margin.left, margin.top + H);
    ctx.lineTo(margin.left + W, margin.top + H);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(margin.left, margin.top);
    ctx.lineTo(margin.left, margin.top + H);
    ctx.stroke();
  }, [sizes, distBySize, selectedPhase, selectedSize, hoveredBar, width, height, colorForRun]);

  const overlayText = showOverlay ? "Loading…" : (!sizes.length ? "No polygon data." : null);
  const disableControls = loading || !sizes.length;

  return (
    <div
      ref={focusRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      style={{ outline: "none", paddingTop: "0rem" }}
      title="Click a bar segment; use ←/→ to navigate runs within the selected phase"
      aria-busy={loading || undefined}
    >
      <style>{`
        .poly-frame { position: relative; }
        .poly-overlay {
          position: absolute; inset: 0;
          display: flex; align-items: center; justify-content: center;
          background: rgba(0,0,0,0.55);
          border-radius: 8px;
          color: #94a3b8;
          font-size: 0.95rem;
          z-index: 2;
          pointer-events: none;
          transition: opacity .12s ease;
        }
        .pill {
          padding: .6rem .5rem;
          border-radius: 999px;
          border: 2px solid #334155;
          background: #0b1220;
          color: #cbd5e1;
          font-size: 1.1rem; font-weight: 600; letter-spacing: .01em;
          white-space: nowrap;
        }
        .pill[disabled] { color: #475569; cursor: not-allowed; }
        .pill.active { background: rgba(96,165,250,0.12); color: #e2e8f0; }
      `}</style>

      {/* Phase selector */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ color: "#94a3b8", fontSize: "1.1rem" }}>Phase:</span>
        {RUN_PHASES.map((ph) => {
          const available = availablePhases.includes(ph);
          const active = selectedPhase === ph && available;
          return (
            <button
              key={ph}
              onClick={() => handlePhaseClick(ph)}
              disabled={!available || disableControls}
              className={`pill${active ? " active" : ""}`}
              title={
                !available ? `No runs for ${RUN_PHASE_LABELS[ph]}` :
                (disableControls ? "Loading…" : `Explore ${RUN_PHASE_LABELS[ph]}`)
              }
            >
              {RUN_PHASE_LABELS[ph]}
            </button>
          );
        })}
      </div>

      {/* Run index nav + size */}
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: "#94a3b8", fontSize: "1.1rem" }}>Idx:</span>
          <button
            onClick={() => jumpRun(-1)}
            disabled={!currentRuns.length || disableControls}
            className="pill"
            title={disableControls ? "Loading…" : "Previous run"}
          >◄</button>
          <code style={{
            minWidth: 50, textAlign: "center", color: "#cbd5e1", fontSize: "1.1rem",
            fontFamily: 'ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono", monospace'
          }}>
            {currentRuns.length ? (currentRuns[selectedRunIdx]?.sample_index ?? "-") : "-"}
          </code>
          <button
            onClick={() => jumpRun(1)}
            disabled={!currentRuns.length || disableControls}
            className="pill"
            title={disableControls ? "Loading…" : "Next run"}
          >►</button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: ".75rem" }}>
          <span style={{ color: "#94a3b8", fontSize: "1.1rem" }}>Polygon size:</span>
          <code style={{
            color: "#cbd5e1", fontSize: "1.1rem",
            fontFamily: 'ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono", monospace'
          }}>
            {selectedSize ?? "-"}
          </code>
        </div>
      </div>

      {/* Chart */}
      <div className="poly-frame">
        {!!overlayText && <div className="poly-overlay" aria-busy={loading || undefined}>{overlayText}</div>}
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          onMouseMove={disableControls ? undefined : onMouseMove}
          onMouseLeave={() => setHoveredBar(null)}
          onClick={disableControls ? undefined : onClick}
          role="img"
          aria-label="Polygon phase distribution by size. Click any colored segment to select."
          style={{
            cursor: hoveredBar && !disableControls ? "pointer" : "default",
            maxWidth: "100%",
            height: "auto",
            display: "block",
            borderRadius: 8,
            filter: loading ? "blur(1.5px) brightness(0.8)" : "none",
            transition: "filter .12s ease"
          }}
        />
      </div>
    </div>
  );
};
